import i18n from "../i18n/config";

function normalizeBase(url = "") {
  return url.trim().replace(/\/$/, "");
}

const RUNTIME_BASE =
  typeof window !== "undefined"
    ? window.__APP_API_BASE_URL || window.__ENV__?.API_BASE_URL
    : "";

const DEFAULT_DEV_BASE = "http://100.95.61.53:8081";

const BASE_URL = normalizeBase(
  process.env.REACT_APP_API_BASE_URL ?? RUNTIME_BASE ?? DEFAULT_DEV_BASE
);

console.log("[apiClient] BASE_URL =", BASE_URL);
console.log("[apiClient] ENV =", process.env.REACT_APP_API_BASE_URL);

function requireBaseUrl() {
  if (!BASE_URL) {
    throw new Error(
      "Missing API base URL. Set REACT_APP_API_BASE_URL or window.__APP_API_BASE_URL"
    );
  }
  return BASE_URL;
}

// Helper function to get language from i18n or localStorage and convert to API format
// This ensures API calls use the same language as the UI
function getLanguageHeader() {
  try {
    // First try to get from i18n (most reliable, syncs with UI)
    // i18n.language will be "th" or "en"
    if (i18n && i18n.language) {
      const i18nLang = i18n.language === "th" ? "th" : "en";
      console.log("[apiClient] getLanguageHeader - from i18n.language:", i18nLang);
      return i18nLang;
    }
    
    // Fallback to localStorage if i18n not available
    const storedLang = localStorage.getItem("lang");
    console.log("[apiClient] getLanguageHeader - localStorage lang:", storedLang);
    if (storedLang === "TH") {
      console.log("[apiClient] getLanguageHeader - returning: th");
      return "th";
    }
    if (storedLang === "EN") {
      console.log("[apiClient] getLanguageHeader - returning: en");
      return "en";
    }
    // Default to "th" if not set
    console.log("[apiClient] getLanguageHeader - default returning: th");
    return "th";
  } catch (e) {
    console.error("[apiClient] getLanguageHeader - error:", e);
    return "th";
  }
}

async function fetchJson(path, options = {}) {
  const base = requireBaseUrl();
  const { langOverride, ...restOptions } = options;
  const lang = langOverride ?? getLanguageHeader();
  console.log("[apiClient] fetchJson - path:", path);
  console.log("[apiClient] fetchJson - lang:", lang, langOverride != null ? "(override)" : "");

  const urlObj = new URL(path, base);
  urlObj.searchParams.set("lang", lang);
  const url = urlObj.pathname + urlObj.search;
  const fullUrl = `${base}${url}`;
  console.log("[apiClient] fetchJson - fullUrl:", fullUrl);

  const res = await fetch(fullUrl, {
    headers: {
      "Accept-Language": lang,
      ...(restOptions.method !== "GET" && {
        "Content-Type": "application/json",
      }),
      ...(restOptions.headers || {}),
    },
    ...restOptions,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status} ${fullUrl}: ${text}`);
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const text = await res.text();
    throw new Error(
      `API ${res.status} ${fullUrl}: expected JSON but got: ${text.slice(0, 200)}`
    );
  }

  return res.json();
}

/**
 * POST multipart/form-data to /api/v1/careers/apply (e.g. name, email, phone, introduction, position?, resume file).
 * Do not set Content-Type so the browser sets multipart/form-data with boundary.
 */
async function fetchMultipartForm(path, formData) {
  const base = requireBaseUrl();
  const lang = getLanguageHeader();
  const urlObj = new URL(path, base);
  urlObj.searchParams.set("lang", lang);
  const url = urlObj.pathname + urlObj.search;
  const fullUrl = `${base}${url}`;
  const res = await fetch(fullUrl, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`API ${res.status} ${fullUrl}: ${text}`);
  }
  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const text = await res.text();
    throw new Error(
      `API ${res.status} ${fullUrl}: expected JSON but got: ${text.slice(0, 200)}`
    );
  }
  return res.json();
}

export const apiCareersApply = (formData) =>
  fetchMultipartForm("/api/v1/careers/apply", formData);

export const apiPromotion = () =>
  fetchJson("/api/v1/news/promotion", { method: "GET" });

export const apiPromotionDetail = (uuid) =>
  fetchJson(`/api/v1/news/promotion/${uuid}`, { method: "GET" });

export const apiListBanners = () =>
  fetchJson("/api/v1/banners/list", { method: "GET" });

export const apiProjectLogos = () =>
  fetchJson("/api/v1/projects/logo", { method: "GET" });

export const apiBrandStory = () =>
  fetchJson("/api/v1/brand/story", { method: "GET" });

export const apiSupremeConcept = () =>
  fetchJson("/api/v1/concepts/list", { method: "GET" });

export const apiNewsPromotion = () =>
  fetchJson("/api/v1/news/overview", { method: "GET" });

export const apiLivingStandards = () =>
  fetchJson("/api/v1/living-standard/overview", { method: "GET" });

export const apiOurCollection = () =>
  fetchJson("/api/v1/projects/list", { method: "GET" });

export const apiNewsAndPromotion = () =>
  fetchJson("/api/v1/news/blogs/list", { method: "GET" });

export const apiLiveAndInvest = () =>
  fetchJson("/api/v1/projects/list", { method: "GET" });

export const apiProjectsList = () =>
  fetchJson("/api/v1/projects/list", { method: "GET" });

export const apiProjectsNew = () =>
  fetchJson("/api/v1/projects/new", { method: "GET" });

export const apiProjectsFilter = () => {
  return fetchJson("/api/v1/projects/filters", { method: "GET" });
};

export const apiProjectsSearch = (params = {}) => {
  const queryParams = new URLSearchParams();
  
  // Language is now sent via header automatically, no need for query parameter
  if (params.listing_type) queryParams.append("listing_type", params.listing_type);
  if (params.name) queryParams.append("name", params.name);
  if (params.location_text) queryParams.append("location_text", params.location_text);
  if (params.project_type) queryParams.append("project_type", params.project_type);
  if (params.price_start !== undefined && params.price_start !== null) {
    queryParams.append("price_start", params.price_start);
  }
  if (params.price_end !== undefined && params.price_end !== null) {
    queryParams.append("price_end", params.price_end);
  }
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.offset !== undefined) queryParams.append("offset", params.offset);
  
  const queryString = queryParams.toString();
  const url = `/api/v1/projects/search${queryString ? `?${queryString}` : ""}`;
  
  return fetchJson(url, { method: "GET" });
};

export const apiNewsBlogsList = (limit = 10, offset = 0) =>
  fetchJson(`/api/v1/news/blogs/list?limit=${limit}&offset=${offset}`, { method: "GET" });

export const apiNewsRecent = () =>
  fetchJson("/api/v1/news/recent", { method: "GET" });

export const apiLivingStandardsDetail = (livingUuid) =>
  fetchJson(`/api/v1/living-standard/${livingUuid}`, { method: "GET" });

/**
 * Fetch related living standards by tags (items that share these tags).
 * Used for the currently displayed item in the carousel / single view.
 * @param {string[]} tags - Tags of the currently viewed item.
 */
export const apiLivingStandardsRelated = (tags = []) => {
  const tagsParam = Array.isArray(tags) && tags.length > 0
    ? tags.map((t) => `tags=${encodeURIComponent(t)}`).join("&")
    : "";
  const queryString = tagsParam ? `?${tagsParam}` : "";
  return fetchJson(`/api/v1/living-standard/related${queryString}`, { method: "GET" });
};

export const apiProjectDetail = (projectUuid) =>
  fetchJson(`/api/v1/projects/${projectUuid}`, { method: "GET" });

export const apiNewsBlogDetail = (uuid) =>
  fetchJson(`/api/v1/news/blogs/${uuid}`, { method: "GET" });

export const apiNewsBlogRelated = (tags = []) => {
  const tagsParam = Array.isArray(tags) && tags.length > 0 
    ? tags.map(t => `tags=${encodeURIComponent(t)}`).join('&')
    : '';
  const queryString = tagsParam ? `?${tagsParam}` : '';
  return fetchJson(`/api/v1/news/blogs/related${queryString}`, { method: "GET" });
};

export const apiSendContactMessage = (payload) =>
  fetchJson("/api/v1/contact/message", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const apiContactPointsList = () => {
  const lang = getLanguageHeader();
  return fetchJson("/api/v1/contact-points/list", { 
    method: "GET",
    headers: {
      "Accept-Language": lang,
      // lang:  lang
    }
  });
};

export const apiContactPointsMessages = (payload) =>
  fetchJson("/api/v1/contact-points/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const apiProjectsAppointments = (payload) =>
  fetchJson("/api/v1/projects/appointments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const apiAboutUsList = () => {
  const lang = getLanguageHeader();
  return fetchJson("/api/v1/about-us/list", { 
    method: "GET",
    headers: {
      "Accept-Language": lang,
      // lang:  lang
    }
  });
};

export const apiVisionMissionsList = () => {
  const lang = getLanguageHeader();
  return fetchJson("/api/v1/vision-missions/list", { 
    method: "GET",
    headers: {
      "Accept-Language": lang,
      // lang:  lang
    }
  });
};

export const apiWhyUsList = (langOverride) =>
  fetchJson("/api/v1/why-us/list", { method: "GET", langOverride });

export const apiCareersList = (langOverride) =>
  fetchJson("/api/v1/careers/list", { method: "GET", langOverride });

export const apiCareerDetail = (uuid, langOverride) =>
  fetchJson(`/api/v1/careers/${uuid}`, { method: "GET", langOverride });

/**
 * @param {string[]} [tags] - Tags from the current career (from /api/v1/careers/{uuid}) to get related positions
 */
export const apiCareersInteresting = (tags = [], langOverride) => {
  const path = "/api/v1/careers/interesting";
  const tagList = Array.isArray(tags) ? tags.filter((t) => t != null && String(t).trim() !== "") : [];
  if (tagList.length === 0) {
    return fetchJson(path, { method: "GET", langOverride });
  }
  const tagsQuery = tagList.map((t) => encodeURIComponent(String(t).trim())).join(",");
  return fetchJson(`${path}?tags=${tagsQuery}`, { method: "GET", langOverride });
};